"use client";

import { useEffect, useState } from "react";
import { Menu, Search, X } from "lucide-react";
import { CartIcon } from "@/components/shop/CartIcon";
import { Link } from "@/components/common/Link";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-6 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-16 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative inline-flex items-center px-4 py-2 text-[0.95rem] font-medium text-foreground bg-transparent hover:bg-transparent rounded-lg transition-colors duration-220 group">
                <span className="relative">
                  {item.label}
                  <span className="absolute left-0 -bottom-1 h-[1.5px] w-0 bg-[var(--chart-2)] transition-all duration-220 ease-out group-hover:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3 px-2">
        <div className="h-8 w-32 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-40 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-28 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-1 px-2">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} onClick={onItemClick} data-index={i} className="text-lg font-default text-foreground px-3 py-3 rounded-lg hover:bg-accent transition-colors duration-200">
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(typeof window !== "undefined" && window.scrollY > 20);
    };
    handleScroll();
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="6140" id="site-navigation-header" className={`sticky top-0 z-50 w-full transition-all duration-240 ease-out bg-background text-foreground ${isScrolled ? "backdrop-blur-lg border-b border-border shadow-sm" : "border-b border-transparent"}`}>
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-4 md:py-5">
        <div className="flex items-center justify-between gap-4">
          {}
          <div className="flex-shrink-0">
            <Link to="/" aria-label="B.Y.E. Hygiene Mist home" className="inline-flex items-center">
              <WvcLogo className="h-8 w-auto" />
            </Link>
          </div>

          {}
          <div className="hidden lg:flex flex-1 justify-center">
            <MenuProvider menu_id="51" className="header-menu">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
            {}
            <div className="hidden md:flex items-center">
              {isSearchOpen ? <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-2 duration-280">
                  <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search hygiene essentials" className="w-56 lg:w-64" />
                  <Button variant="ghost" size="icon" onClick={() => {
                setIsSearchOpen(false);
                setSearchValue("");
              }} aria-label="Close search" className="text-foreground hover:bg-accent rounded-lg">
                    <X className="size-4" />
                  </Button>
                </div> : <Button variant="ghost" size="icon" onClick={() => setIsSearchOpen(true)} aria-label="Open search" className="text-foreground hover:bg-accent rounded-lg">
                  <Search className="size-5" />
                </Button>}
            </div>

            {}
            <div className="relative">
              <CartIcon variant="ghost" size="icon" iconSize="size-5" className="text-primary hover:bg-accent rounded-lg" />
            </div>

            {}
            <div className="lg:hidden">
              <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Open menu" className="text-foreground hover:bg-accent rounded-lg">
                    <Menu className="size-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full sm:max-w-sm bg-background text-foreground p-0 flex flex-col gap-0">
                  <SheetHeader className="p-6 border-b border-border">
                    <SheetTitle className="text-left text-foreground font-default">
                      Navigation
                    </SheetTitle>
                  </SheetHeader>

                  <div className="px-4 py-5">
                    <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search hygiene essentials" className="w-full" />
                  </div>

                  <div className="flex-1 overflow-y-auto pb-6">
                    <MenuProvider menu_id="51" className="mobile-header-menu">
                      <MobileMenuConsumer onItemClick={() => setIsMobileOpen(false)} />
                    </MenuProvider>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </section>;
}